/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { useState, useEffect } from 'react';
import { Icon, Input, message, Modal } from 'antd';
import { createInvite, getInvites, sendInviteEmail } from '../../_shared/services/accounts.service';
import { deleteUser as DeleteUser } from '../../_shared/services/manage-users.service';
import { get } from 'lodash';
import { useAuth } from '../../_shared/hooks/auth';
import { emailRegex, searchTable } from '../../actions/utils';
import { TableList, TableListHeader } from '../../_shared/table-list';
import { watcherstyles } from './styles'; 
import moment from 'moment';
import { Button } from '../../_shared/button';

// Card #277 — hoisted out of the JSX so the search box below can walk the same
// definitions the grid renders. The two invite flags and the date carry explicit hooks
// because their cells show something other than the stored value: a formatted date, and
// the literal words "true"/"false" in place of a boolean.
const COLS = (opts = {}) => ([
  {
    title: 'Email',
    dataIndex: 'email',
    width: '30%'
  },
  {
    title: 'Date Invited',
    dataIndex: 'date',
    width: '30%',
    inputType: 'date',
    render: t => moment(t).format('L'),
  },
  {
    title: 'Accepted Invite',
    dataIndex: 'data.accepted',
    width: '15%',
    searchValue: r => !get(r, 'data.accepted') ? 'false' : 'true',
    render: t => !t ? 'false' : 'true'
  },
  {
    title: 'Received Invite',
    dataIndex: 'data.notified',
    width: '15%',
    searchValue: r => !get(r, 'data.notified') ? 'false' : 'true',
    render: t => !t ? 'false' : 'true'
  },
  {
    title: 'Actions',
    dataIndex: 'data.accepteds',
    width: '10%',
    render: (t, r) => {
      const accepted = !!get(r, 'data.accepted');
      return (
        <div css={css(watcherstyles.buttonActions)}>
          {(accepted && r.invited) && <Button type='primary' onClick={() => opts.deleteUser(r)} hoverText="Delete User"><Icon type="delete" /></Button>}
          {(!accepted && !get(r, 'data.notified')) && <Button type='primary' onClick={() => opts.resendInviteEmail(r)} hoverText="Resend Email"><Icon type="mail" /></Button>}
        </div>
      )
    }
  }
])

export const Invite = props => {
  const [state, setState] = useState({
    sending: false,
    loading: false,
    email: '',
    search: '',
    invites: [],
  })
  
  const { user } = useAuth();

  const retrieveInvites = async () => {
    try {
      setState(s => ({ ...s, loading: true }));
      const invites = await getInvites({ userId: user.id })
      setState(s => ({ ...s, invites }));
    } catch (err) {
      const error = get(err, 'response.data.error', err.message);
      message.error(`${error}`);
    } finally {
      setState(s => ({ ...s, loading: false }));
    }
  }

  const onChange = e => {
    const { value } = e.target;
    setState(s => ({ ...s, email: value }))
  }

  // Read the value out of the event BEFORE the updater — the same shape as onChange above.
  // A functional setState runs after the handler returns, by which point React 16 has
  // recycled the synthetic event and nulled its target, so `e.target.value` inside the
  // updater throws and unmounts the page on the second keystroke.
  const changeSearch = e => {
    const { value } = e.target;
    setState(s => ({ ...s, search: value }))
  }

  const existsInList = email => {
    return !!state.invites.find(u => u.email == email && !get(u, 'data.notified'));
  }

  const sendInvite = async () => {
    try {
      const email = state.email.trim().toLowerCase();

      if (!emailRegex.test(email)) {
        return message.error('Email address is invalid');
      }

      if (existsInList(email)) {
        return message.error('You have already invited this user');
      }

      setState(s => ({ ...s, sending: true }));
      await createInvite({ 
        email,
        userId: user.id,
        data: {
          accepted: false,
        },
      });

      message.success('User invited successfully!');
    } catch (err) {
      const error = get(err, 'response.data.error', err.message);
      message.error(`${error}`);
    } finally {
      setState(s => ({ ...s, sending: false }));
    }
  }

  const resendInviteEmail = async (row) => {
    try {
      setState(s => ({ ...s, sending: true }));
      await sendInviteEmail({ 
        email: row.email,
        userId: user.id,
        data: {},
      });

      message.success("Email sent successfully! Please tell the user to check their spam folder if it doesn't show up within the next 30 minutes");
    } catch (err) {
      const error = get(err, 'response.data.error', err.message);
      message.error(`${error}`);
    } finally {
      setState(s => ({ ...s, sending: false }));
    }
  }

  const deleteUser = async (user) => {
    const { email, invited } = user;
    Modal.confirm({
      title: 'Are you sure?',
      content: `Are you sure you want to delete ${email}? This cannot be un-done.`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          await DeleteUser(invited);
          retrieveInvites();  
        } catch (err) {
          message.error(`Failed to delete user: ${err.message}`);
        }
      },
      onCancel: () => {}
    });
  }

  useEffect(() => {
    retrieveInvites();
  }, []);

  const shown = searchTable(COLS(), state.invites, state.search);

  return (
    <div>
      <div>Invite Users</div>

      <Input.Search 
        value={state.email} 
        onChange={onChange}
        enterButton="Invite"
        placeholder="Enter email address" 
        loading={state.sending} 
        onSearch={sendInvite}
      />

      <TableListHeader>
        <span style={{ marginRight: 10 }}>Invited Users</span>
        <Input.Search
          placeholder="Search all columns"
          value={state.search}
          allowClear
          style={{ width: '40%' }}
          onChange={changeSearch}
        />
      </TableListHeader>

      <TableList
        data={shown}
        canSelectRows={false}
        columns={COLS({ deleteUser, resendInviteEmail })}
      />
    </div>
  )
}